import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, QrCode, KeyRound, RefreshCw } from "lucide-react";
import { RecoveryCodes } from "@/components/RecoveryCodes";

export default function Security() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null); // {qr, secret}
  const [code, setCode] = useState("");
  const [disCode, setDisCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [regenCode, setRegenCode] = useState("");
  const [showRegen, setShowRegen] = useState(false);

  const load = () => api.get("/auth/2fa/status").then(({ data }) => setStatus(data));
  useEffect(() => { load(); }, []);

  const beginSetup = async () => {
    try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const enable = async () => {
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      toast.success("Two-factor authentication enabled");
      setSetup(null); setCode("");
      if (data.recovery_codes) setCodes(data.recovery_codes);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const disable = async () => {
    try { await api.post("/auth/2fa/disable", { code: disCode }); toast.success("Two-factor authentication disabled"); setDisCode(""); setCodes(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const regenerate = async () => {
    try {
      const { data } = await api.post("/auth/2fa/recovery/regenerate", { code: regenCode });
      toast.success("Recovery codes regenerated");
      setRegenCode(""); setShowRegen(false); setCodes(data.recovery_codes); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div><h1 className="font-serif text-4xl">Security</h1><p className="text-[color:var(--ink-2)]">Protect your account with an authenticator app (TOTP).</p></div>

      <div className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${status?.enabled ? "bg-[color:var(--olive-light)]" : "bg-[color:var(--sage-light)]"}`}>
            {status?.enabled ? <ShieldCheck className="text-[color:var(--olive)]" /> : <ShieldOff className="text-[color:var(--muted)]" />}
          </div>
          <div>
            <div className="font-semibold">Two-Factor Authentication</div>
            <div className="text-sm text-[color:var(--ink-2)]" data-testid="twofa-status">{status?.enabled ? "Enabled — required at every sign-in." : "Disabled."}</div>
          </div>
        </div>

        {!status?.enabled && !setup && (
          <button className="btn btn-primary" onClick={beginSetup} data-testid="twofa-setup"><QrCode size={16} /> Set up authenticator</button>
        )}

        {!status?.enabled && setup && (
          <div className="fadeup">
            <p className="text-sm text-[color:var(--ink-2)] mb-3">Scan this QR code with Google Authenticator, 1Password, or Authy, then enter the 6-digit code to confirm.</p>
            <img src={setup.qr} alt="2FA QR" className="w-44 h-44 rounded-lg border border-[color:var(--line)] bg-white p-2" data-testid="twofa-qr" />
            <p className="text-xs text-[color:var(--muted)] mt-2 font-mono break-all">Secret: {setup.secret}</p>
            <div className="flex gap-2 mt-4 max-w-xs">
              <input className="field font-mono tabnum text-center tracking-[0.3em]" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" data-testid="twofa-enable-code" />
              <button className="btn btn-primary px-6" disabled={code.length !== 6} onClick={enable} data-testid="twofa-enable">Verify</button>
            </div>
          </div>
        )}

        {status?.enabled && (
          <div className="fadeup">
            <p className="text-sm text-[color:var(--ink-2)] mb-3">Enter a current code to turn off 2FA.</p>
            <div className="flex gap-2 max-w-xs">
              <input className="field font-mono tabnum text-center tracking-[0.3em]" inputMode="numeric" maxLength={6} value={disCode} onChange={(e) => setDisCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" data-testid="twofa-disable-code" />
              <button className="btn btn-danger px-6" disabled={disCode.length !== 6} onClick={disable} data-testid="twofa-disable">Disable</button>
            </div>
          </div>
        )}
      </div>

      {codes && <div className="card p-6"><RecoveryCodes codes={codes} onDone={() => setCodes(null)} doneLabel="Done" /></div>}

      {status?.enabled && !codes && (
        <div className="card p-6" data-testid="recovery-panel">
          <div className="flex items-center gap-3 mb-2"><KeyRound className="text-[color:var(--olive)]" size={18} /><div className="font-semibold">Recovery codes</div></div>
          <p className="text-sm text-[color:var(--ink-2)] mb-3"><b data-testid="recovery-remaining">{status.recovery_codes_remaining ?? 0}</b> unused recovery codes remaining. Regenerating replaces all existing codes.</p>
          {!showRegen ? (
            <button className="btn btn-secondary h-10" onClick={() => setShowRegen(true)} data-testid="recovery-regenerate-open"><RefreshCw size={15} /> Regenerate codes</button>
          ) : (
            <div className="flex gap-2 max-w-xs">
              <input className="field font-mono tabnum text-center tracking-[0.3em]" inputMode="numeric" maxLength={6} value={regenCode} onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" data-testid="recovery-regen-code" />
              <button className="btn btn-primary px-6" disabled={regenCode.length !== 6} onClick={regenerate} data-testid="recovery-regenerate">Confirm</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
